import { FormattedMessage } from 'react-intl';
import { useState } from 'react';
import SectionTitle from '../layout/SectionTitle';
import { type UserProfile } from '../../../utils/api/schema/profileSchema';
import { paramsKeys } from '../../../utils/consts/params';
import { profileSections } from '../profileSections';
import { useActiveSectionContext } from '../ActiveSectionContext';
import { maxDeviceLimit } from '../../../utils/consts/profileConsts';
import DeviceManagementBlock from './DeviceManagementBlock';
import AutoLoginBlock from './AutoLoginBlock';
import RouterSettingsSection from './components/RouterSettingsSection';
import styles from './DeviceManagementSection.scss';

const generatePlaceholderRouterName = (devices: UserProfile['devices'], maxDevices: number): string => {
  const placeholderRouterNamePrefix = 'Router ';
  const routerIndexingOffset = 2;
  const routerDevices = devices.filter((d) => d.platform === 'router');

  for (let i = 1; i <= maxDevices + 1; i++) {
    const routerName = `${placeholderRouterNamePrefix}${i}`;

    if (!routerDevices.some((d) => d.name === routerName)) {
      return routerName;
    }
  }

  return `${placeholderRouterNamePrefix}${maxDevices + routerIndexingOffset}`;
};

interface DeviceManagementSectionProps {
  profile: UserProfile;
  onAddNewDeviceClick: () => void;
  handleDeviceListUpdate: (devices: UserProfile['devices']) => void;
  premiumLink: string;
}

const DeviceManagementSection = ({
  profile,
  onAddNewDeviceClick,
  handleDeviceListUpdate,
  premiumLink,
}: DeviceManagementSectionProps) => {
  const { devices } = profile;

  const { isRouterDeviceManagementSection, setIsRouterDeviceManagementSection } = useActiveSectionContext();

  const isSetappSubscription = profile.type === 'setapp';
  const hasActiveSubscription = profile.subscription?.hasActiveSubscription;

  const [editedDeviceId, setEditedDeviceId] = useState<{ id: string; name: string } | null>(null);
  const onRouterConfigureClick = (routerId: string, routerName: string) => {
    setEditedDeviceId({ id: routerId, name: routerName });
    setIsRouterDeviceManagementSection(true);
  };

  const maxDevices = isSetappSubscription ? maxDeviceLimit.SETAPP : maxDeviceLimit.DEFAULT;

  return isRouterDeviceManagementSection ? (
    <RouterSettingsSection
      editedRouter={editedDeviceId}
      routerPlaceholderName={generatePlaceholderRouterName(devices, maxDevices)}
      hasActiveSubscription={hasActiveSubscription ?? false}
      premiumLink={premiumLink}
      onBackClick={(withReload = false) => {
        if (withReload) {
          location.href = `/profile?${paramsKeys.PROFILE_SECTION}=${profileSections.DEVICES}`;
        } else {
          setIsRouterDeviceManagementSection(false);
          setEditedDeviceId(null);
        }
      }}
    />
  ) : (
    <>
      <SectionTitle className={styles.sectionTitle}>
        <FormattedMessage id="profile.deviceManagement.title" defaultMessage="Devices management" />
        <span className={styles.deviceCounter}>
          <FormattedMessage
            id="profile.deviceManagement.connectedDevicesCounter"
            defaultMessage="(Connected {devices, number} out of {maxDevices, number})"
            values={{ devices: devices.length, maxDevices }}
          />
        </span>
      </SectionTitle>
      <DeviceManagementBlock
        isSetappSubscription={isSetappSubscription}
        devices={devices}
        onAddNewDeviceClick={onAddNewDeviceClick}
        onRouterConfigureClick={onRouterConfigureClick}
        onDeviceRemoved={(removedDeviceId) => {
          handleDeviceListUpdate(devices.filter((d) => d.id !== removedDeviceId));
        }}
      />
      <AutoLoginBlock isSetappSubscription={isSetappSubscription} />
    </>
  );
};

export default DeviceManagementSection;
